import React, { ReactElement, useEffect, useState } from 'react';
import { Avatar, Tooltip } from 'antd';

import { getLatestDocumentForType } from 'leash/utilities/healthDocuments/healthDocuments';
import type { HealthDocument } from 'leash/types';
import {
  HealthDocumentTypeLabels,
  HealthDocumentWorkflowStatusLabel,
  HealthDocumentTypeId,
  HealthDocumentWorkflowStatusId,
} from 'leash/types';

interface Props {
  documentType: HealthDocumentTypeId;
  documents: HealthDocument[];
}

const documentWorkflowStatusStyles: Record<
  keyof typeof HealthDocumentWorkflowStatusId | string,
  React.CSSProperties
> = {
  NotUploaded: { backgroundColor: '#fff', color: '#ccc' },
  PendingReview: { backgroundColor: '#fff', color: '#666' },
  HealthReview: { backgroundColor: '#fff', color: '#666' },
  Approved: { backgroundColor: '#f5faf2', color: '#66af37' },
  Rejected: { backgroundColor: '#fff5f5', color: '#ff7272' },
  AwaitingDisclosure: { backgroundColor: '#f8f3ff', color: '#9747ff' },
};

function HealthDocumentStatusBadge({ documentType, documents }: Props): ReactElement {
  const [document, setDocument] = useState<HealthDocument | null>();

  useEffect(() => {
    if (!documents.length || !documentType) {
      return;
    }

    setDocument(getLatestDocumentForType(documents, documentType));
  }, [documents, documentType]);

  const label = HealthDocumentTypeLabels[HealthDocumentTypeId[documentType]];

  if (!document?.workflowEntity) {
    return (
      <Tooltip placement="top" title={HealthDocumentWorkflowStatusLabel.NotUploaded}>
        <Avatar
          size={32}
          style={{ ...documentWorkflowStatusStyles.NotUploaded, marginRight: 0 }}
          gap={documentType === HealthDocumentTypeId.HealthCertificate ? 7 : 4}
        >
          {label}
        </Avatar>
      </Tooltip>
    );
  }

  const tooltipLabel =
    HealthDocumentWorkflowStatusLabel[
      HealthDocumentWorkflowStatusId[document.workflowEntity.currentWorkflowStatusId]
    ];
  const style =
    documentWorkflowStatusStyles[
      HealthDocumentWorkflowStatusId[document.workflowEntity.currentWorkflowStatusId]
    ];

  return (
    <Tooltip placement="top" title={tooltipLabel}>
      <Avatar
        size={32}
        style={{ ...style, marginRight: 0 }}
        gap={documentType === HealthDocumentTypeId.HealthCertificate ? 7 : 4}
      >
        {label}
      </Avatar>
    </Tooltip>
  );
}

export default HealthDocumentStatusBadge;
